import { useCallback, useMemo, useState } from 'react';
import { Content, ContentNode } from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { deepCloneContent } from '../../model/CloneContent';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { findParentPage } from '../workflow-viewer/content/FindParentPage';
import {
  movePageUp,
  movePageDown,
  movePageInto,
  listPages,
  isWithinPageDepthLimit,
} from '../../services/PageOperations';
import { Page, PagePosition } from '../../model/Page';

/** Presentation state for the "Edit page" modal in the content navigator. */
export interface EditPageButtonViewModel {
  readonly isModalVisible: boolean;
  /** List of pages from the working content copy being reordered inside the modal. */
  readonly pages: readonly Page[];
  /** Currently selected page in the tree, or undefined when nothing is selected. */
  readonly selectedPage: ContentNode | undefined;
  /** True when Move Up is allowed for the current selection. */
  readonly isMoveUpEnabled: boolean;
  /** True when Move Down is allowed for the current selection. */
  readonly isMoveDownEnabled: boolean;
  /** True when Move Into (nest under next sibling) is allowed. */
  readonly isMoveIntoEnabled: boolean;
  /** True while the reordered content is being saved. */
  readonly isSaving: boolean;
  /** Inline error to display after a failed reorder attempt. Undefined when no error. */
  readonly reorderErrorMessage: string | undefined;
  readonly openModal: () => void;
  readonly dismissModal: () => void;
  /** Persist the reordered content and close the modal. */
  readonly confirmReorder: () => void;
  /** Called when the user selects a page in the tree. */
  readonly selectPage: (page: ContentNode, depth: number) => void;
  readonly moveUp: () => void;
  readonly moveDown: () => void;
  readonly moveInto: () => void;
}

export function useEditPageButtonViewModel(): EditPageButtonViewModel {
  const service = useContentEditorService();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [workingContent, setWorkingContent] = useState<Content | undefined>(undefined);
  const [selectedPage, setSelectedPage] = useState<ContentNode | undefined>(undefined);
  const [position, setPosition] = useState<PagePosition | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [reorderErrorMessage, setReorderErrorMessage] = useState<string | undefined>(undefined);

  const pages = useMemo(() => (workingContent ? listPages(workingContent) : []), [workingContent]);
  const isMoveUpEnabled = selectedPage !== undefined;
  const isMoveDownEnabled = selectedPage !== undefined;
  const isMoveIntoEnabled =
    selectedPage !== undefined && (position ? isWithinPageDepthLimit(position.depth) : false);

  const openModal = useCallback(() => {
    const content = service.content;
    if (!content) return;
    setWorkingContent(deepCloneContent(content));
    setSelectedPage(undefined);
    setPosition(undefined);
    setReorderErrorMessage(undefined);
    setIsModalVisible(true);
  }, [service]);

  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    setWorkingContent(undefined);
    setSelectedPage(undefined);
    setPosition(undefined);
    setIsSaving(false);
    setReorderErrorMessage(undefined);
  }, []);

  const selectPage = useCallback(
    (page: ContentNode, depth: number) => {
      if (!workingContent) return;
      const parentInfo = findParentPage(workingContent.contentData, page);
      if (!parentInfo) return;
      setSelectedPage(page);
      setPosition({ parent: parentInfo.parent, index: parentInfo.pageIdx, depth });
    },
    [workingContent],
  );

  const moveUp = useCallback(() => {
    if (!selectedPage || !workingContent || !position) return;
    const result = movePageUp(workingContent.contentData, position);
    if (result) {
      setWorkingContent({ ...workingContent, contentData: result.root });
      setPosition(result.position);
    }
  }, [selectedPage, workingContent, position]);

  const moveDown = useCallback(() => {
    if (!selectedPage || !workingContent || !position) return;
    const result = movePageDown(workingContent.contentData, position);
    if (result) {
      setWorkingContent({ ...workingContent, contentData: result.root });
      setPosition(result.position);
    }
  }, [selectedPage, workingContent, position]);

  const moveInto = useCallback(() => {
    if (!selectedPage || !workingContent || !position) return;
    const result = movePageInto(workingContent.contentData, position);
    if (result) {
      setWorkingContent({ ...workingContent, contentData: result.root });
      setPosition(result.position);
    }
  }, [selectedPage, workingContent, position]);

  const confirmReorder = useCallback(() => {
    if (!workingContent || isSaving) return;
    setIsSaving(true);
    setReorderErrorMessage(undefined);
    void (async () => {
      try {
        service.stageEdit(workingContent);
        await service.flush();
        dismissModal();
      } catch (error) {
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save reordered pages to working copy');
        setReorderErrorMessage(
          'There was an error reordering pages. Please wait a few seconds and retry.',
        );
      } finally {
        setIsSaving(false);
      }
    })();
  }, [workingContent, isSaving, service, dismissModal]);

  return {
    isModalVisible,
    pages,
    selectedPage,
    isMoveUpEnabled,
    isMoveDownEnabled,
    isMoveIntoEnabled,
    isSaving,
    reorderErrorMessage,
    openModal,
    dismissModal,
    confirmReorder,
    selectPage,
    moveUp,
    moveDown,
    moveInto,
  };
}
